import type { BodyBlock, CardRow } from '@shared/domain/card.ts';
import type { Rounding } from '@shared/domain/rounding.ts';

export type CardListItem = CardRow & {
  last_app_write_at?: string | null;
  /** Qué hay que hacer (fila «Comentario» o primer párrafo del cuerpo); falta en cachés de v1. */
  summary?: string | null;
};

export interface CardDetail extends CardListItem {
  body: BodyBlock[] | null;
  body_expires_at: string | null;
  body_synced_at: string | null;
}

export type Role = 'admin' | 'member';

export interface Profile {
  id: string;
  email: string;
  display_name: string | null;
  role: Role;
  notion_user_id: string | null;
  solo_lo_mio: boolean;
  redondeo: Rounding;
}

export interface PersonSummary {
  id: string;
  email: string;
  display_name: string | null;
  notion_user_id: string | null;
}

export interface NotionUser {
  id: string;
  name: string | null;
  email: string | null;
  avatar_url: string | null;
}

/** Persona de Notion en la pantalla de vínculo (RPC notion_people_for_link). */
export interface LinkCandidate {
  id: string;
  name: string | null;
  email: string | null;
  /** Ya vinculada a otra cuenta de Tablero. */
  linked: boolean;
  open_cards: number;
  total_cards: number;
  /** Hasta 3 tarjetas donde figura en Miembros (abiertas primero). */
  sample_cards: string[];
}

export interface TimeEntry {
  id: string;
  user_id: string;
  card_id: string;
  started_at: string;
  ended_at: string | null;
  source: 'cronometro' | 'manual';
  note: string | null;
  needs_review: boolean;
  warned_at: string | null;
  deleted_at: string | null;
  created_at?: string;
  updated_at?: string;
  /** Creado o modificado sin conexión; aún no confirmado por el servidor. */
  pending?: boolean;
}

export interface SyncStatus {
  last_success_at: string | null;
  last_run: {
    id: string;
    mode: string;
    trigger: string;
    status: 'running' | 'success' | 'error' | 'skipped';
    started_at: string;
    finished_at: string | null;
    error: string | null;
  } | null;
}

export type SyncTrigger = 'apertura' | 'boton' | 'gesto';

export const CARD_LIST_COLUMNS =
  'id, name, status, status_group, status_rank, is_closed, priority_rank, miembros, lider_proyecto, etiquetas, fechas_start, fechas_end, fechas, marca, tipo, proyecto, figma_dev_mode, figma_prototipo, fecha_creacion, horas_estimadas, horas_trabajadas, notion_url, notion_last_edited, summary';

export const ENTRY_COLUMNS =
  'id, user_id, card_id, started_at, ended_at, source, note, needs_review, warned_at, deleted_at, created_at, updated_at';

/** Registro de tiempo por persona, tarjeta y día (v2). El cronómetro suma; registrar fija. */
export interface WorkLog {
  id: string;
  user_id: string;
  card_id: string;
  /** Fecha local America/Santiago, 'YYYY-MM-DD'. */
  work_date: string;
  seconds: number;
  needs_review: boolean;
  deleted_at: string | null;
  updated_at?: string;
  /** Creado o modificado sin conexión; aún no confirmado por el servidor. */
  pending?: boolean;
}

export const WORK_LOG_COLUMNS =
  'id, user_id, card_id, work_date, seconds, needs_review, deleted_at, updated_at';
